import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Printer, FileSpreadsheet, BookmarkPlus, Loader2 } from "lucide-react";
import { API } from "../../config/api";

export const ReportExportButtons = ({ report, onSaved }) => {
  const [busy, setBusy] = useState(null);
  if (!report?.report_id) return null;

  const downloadExcel = async () => {
    setBusy("excel");
    try {
      const r = await axios.get(`${API}/reports/${report.report_id}/export?format=excel`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.report_name.replace(/\s/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Excel export failed");
    } finally {
      setBusy(null);
    }
  };

  const openPrint = async () => {
    setBusy("pdf");
    try {
      const r = await axios.get(`${API}/reports/${report.report_id}/export?format=pdf`, {
        responseType: "text",
      });
      const win = window.open("", "_blank");
      if (!win) {
        toast.error("Popup blocked — allow popups to print this report");
        return;
      }
      win.document.write(r.data);
      win.document.close();
      // Give the new window a moment to render before opening the print dialog
      setTimeout(() => win.print(), 400);
    } catch (e) {
      toast.error("Print preview failed");
    } finally {
      setBusy(null);
    }
  };

  const saveSnapshot = async () => {
    const name = window.prompt("Name this saved report:", `${report.report_name} — ${new Date().toLocaleDateString()}`);
    if (!name) return;
    setBusy("save");
    try {
      await axios.post(`${API}/reports/saved-views`, { report_id: report.report_id, name });
      toast.success("Report saved");
      onSaved?.();
    } catch (e) {
      toast.error("Could not save report");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="report-export-buttons" className="flex items-center gap-2 flex-wrap">
      <Button
        data-testid="export-pdf-btn"
        onClick={openPrint}
        disabled={busy !== null}
        variant="outline"
        className="border-slate-300"
      >
        {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Printer className="w-4 h-4 mr-1.5" />}
        Export PDF
      </Button>
      <Button
        data-testid="export-excel-btn"
        onClick={downloadExcel}
        disabled={busy !== null}
        variant="outline"
        className="border-slate-300"
      >
        {busy === "excel" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="w-4 h-4 mr-1.5" />}
        Export Excel
      </Button>
      <Button
        data-testid="save-report-btn"
        onClick={saveSnapshot}
        disabled={busy !== null}
        className="bg-slate-900 hover:bg-slate-800 text-white"
      >
        {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <BookmarkPlus className="w-4 h-4 mr-1.5" />}
        Save
      </Button>
    </div>
  );
};

export default ReportExportButtons;
